/**
 * SkillSystem.ts
 * Defines and executes per-band-member active skills.
 *
 * Each of the five band classes has one skill triggered by pressing Q
 * (or clicking the SKILL button) when that unit is selected.
 *
 *   Protector  (Steve)  — Power Chord  : stun all nearby enemies for 2.5s
 *   Promoter   (Ali)    — Crowd Hype   : speed boost (×1.6) for nearby allies
 *   Engine     (Chris)  — Tempo Surge  : attack-rate boost (×1.75) for ALL allies
 *   Sustain    (Alex)   — Bassline     : heal 30 % maxHp for nearby allies
 *   Mystic     (Jim)    — Prophecy     : reveal a 10-tile fog radius instantly
 *   Songwriter (Nigel)  — Future Verse : stun nearby enemies 1.5s + heal nearby allies 20 % maxHp
 *
 * All skill effects are purely data-writes — no graphics here. Visual feedback
 * (buff rings on units, pulse from beacon) is handled by UnitRenderer and
 * MapRenderer respectively.
 */

import type { Unit }           from '../entities/Unit';
import type { FogSystem }      from './FogSystem';
import type { ResourceSystem } from './ResourceSystem';
import type { BandClass }      from '../types';
import { Faction }             from '../types';
import {
  SKILL_COOLDOWN_PROTECTOR,
  SKILL_COOLDOWN_PROMOTER,
  SKILL_COOLDOWN_ENGINE,
  SKILL_COOLDOWN_SUSTAIN,
  SKILL_COOLDOWN_MYSTIC,
  SKILL_COOLDOWN_SONGWRITER,
  SKILL_STUN_DURATION,
  SKILL_STUN_RADIUS,
  SKILL_HYPE_DURATION,
  SKILL_HYPE_RADIUS,
  SKILL_SURGE_DURATION,
  SKILL_HEAL_RATIO,
  SKILL_HEAL_RADIUS,
  SKILL_PROPHECY_RADIUS,
  SKILL_VERSE_STUN_DURATION,
  SKILL_VERSE_HEAL_RATIO,
  SKILL_VERSE_RADIUS,
  SKILL_HYPE_FAR_THRESHOLD,
  SKILL_HYPE_FAR_REVEAL,
  SKILL_PROPHECY_FAR_THRESHOLD,
  SKILL_PROPHECY_FAR_RADIUS,
  SKILL_BASSLINE_VENUE_RANGE,
  SKILL_BASSLINE_VENUE_SALES,
  SKILL_BASSLINE_VENUE_GEAR,
} from '../constants';

// ── Skill metadata ────────────────────────────────────────────────────────────

export const SKILL_NAMES: Partial<Record<BandClass, string>> = {
  protector:  'Power Chord',
  promoter:   'Crowd Hype',
  engine:     'Tempo Surge',
  sustain:    'Bassline',
  mystic:     'Prophecy',
  songwriter: 'Future Verse',
};

export const SKILL_DESCRIPTIONS: Partial<Record<BandClass, string>> = {
  protector:  'Stun all nearby enemies',
  promoter:   'Speed boost for nearby allies',
  engine:     'Attack boost for all allies',
  sustain:    'Heal nearby allies',
  mystic:     'Reveal a large fog area',
  songwriter: 'Stun nearby enemies + heal nearby allies',
};

export const SKILL_COOLDOWNS: Partial<Record<BandClass, number>> = {
  protector:  SKILL_COOLDOWN_PROTECTOR,
  promoter:   SKILL_COOLDOWN_PROMOTER,
  engine:     SKILL_COOLDOWN_ENGINE,
  sustain:    SKILL_COOLDOWN_SUSTAIN,
  mystic:     SKILL_COOLDOWN_MYSTIC,
  songwriter: SKILL_COOLDOWN_SONGWRITER,
};

/** Colour of the beacon pulse triggered by each skill. */
export const SKILL_PULSE_COLOUR: Partial<Record<BandClass, number>> = {
  protector:  0x4488ff, // blue   — electric guitar shock
  promoter:   0xffd700, // gold   — crowd energy
  engine:     0xff8833, // orange — rhythm surge
  sustain:    0x44cc66, // green  — healing bassline
  mystic:     0xaa44ff, // purple — prophetic reveal
  songwriter: 0xe8c84a, // warm gold — future transmission
};

// ── Context passed in from Game.ts ────────────────────────────────────────────

export interface SkillContext {
  allUnits:     Unit[];
  fogSystem:    FogSystem;
  triggerPulse: (colour: number, duration?: number) => void;
  // Distance-bonus mechanics (all optional for backwards-compat with tests)
  beaconPos?:            { col: number; row: number };
  resources?:            ResourceSystem;
  playerVenuePositions?: { col: number; row: number }[];
  showToast?:            (msg: string, sev: 'info' | 'success' | 'warning' | 'danger') => void;
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Returns true if `unit` has an active skill AND it is ready to use.
 * Fans and the source (Nigel) have no skill.
 */
export function canUseSkill(unit: Unit): boolean {
  if (!unit.bandClass) return false;
  if (unit.bandClass === 'fan' || unit.bandClass === 'source') return false;
  if (unit.isDowned || !unit.alive) return false;
  return unit.skillCooldown <= 0;
}


/**
 * Fire the caster's skill.
 * Assigns the cooldown and calls the appropriate effect function.
 * Returns true if the skill actually fired; false if cooldown is still running
 * or the unit has no skill.
 */
export function activateSkill(caster: Unit, ctx: SkillContext): boolean {
  if (!canUseSkill(caster)) return false;
  const bc       = caster.bandClass!;
  const cooldown = SKILL_COOLDOWNS[bc];
  if (!cooldown) return false;

  switch (bc) {
    case 'protector':  powerChord(caster, ctx);  break;
    case 'promoter':   crowdHype(caster, ctx);   break;
    case 'engine':     tempoSurge(caster, ctx);  break;
    case 'sustain':    bassline(caster, ctx);    break;
    case 'mystic':     prophecy(caster, ctx);    break;
    case 'songwriter': futureVerse(caster, ctx); break;
    default:           return false;
  }

  caster.skillCooldown = cooldown;
  return true;
}

// ── Skill implementations ─────────────────────────────────────────────────────

/**
 * Power Chord (Protector — Steve)
 * Stun all enemies within SKILL_STUN_RADIUS tiles for SKILL_STUN_DURATION seconds.
 */
function powerChord(caster: Unit, ctx: SkillContext): void {
  for (const u of ctx.allUnits) {
    if (u.faction === Faction.Player || !u.alive) continue;
    if (tileDist(caster, u) <= SKILL_STUN_RADIUS) {
      u.stunTimer = Math.max(u.stunTimer, SKILL_STUN_DURATION);
    }
  }
  ctx.triggerPulse(SKILL_PULSE_COLOUR.protector!, 1.8);
}

/**
 * Crowd Hype (Promoter — Ali)
 * Speed boost for nearby allies. When Ali is ≥ SKILL_HYPE_FAR_THRESHOLD tiles
 * from the Beacon, also reveals a fog radius (Tour Advance bonus).
 */
function crowdHype(caster: Unit, ctx: SkillContext): void {
  for (const u of ctx.allUnits) {
    if (u.faction !== Faction.Player || !u.alive || u.isDowned) continue;
    if (tileDist(caster, u) <= SKILL_HYPE_RADIUS) {
      u.speedBoostTimer = Math.max(u.speedBoostTimer, SKILL_HYPE_DURATION);
    }
  }
  if (ctx.beaconPos) {
    const dist = Math.hypot(
      caster.gridPos.col - ctx.beaconPos.col,
      caster.gridPos.row - ctx.beaconPos.row,
    );
    if (dist >= SKILL_HYPE_FAR_THRESHOLD) {
      ctx.fogSystem.revealRadius(caster.gridPos.col, caster.gridPos.row, SKILL_HYPE_FAR_REVEAL);
      ctx.showToast?.('Ali: Tour Advance! (fog revealed)', 'success');
    }
  }
  ctx.triggerPulse(SKILL_PULSE_COLOUR.promoter!, 1.8);
}

/**
 * Tempo Surge (Engine — Chris)
 * Boost attack rate by SKILL_ATTACK_BOOST_MULT for ALL friendly units for
 * SKILL_SURGE_DURATION seconds. No radius — the whole band feels the groove.
 */
function tempoSurge(caster: Unit, ctx: SkillContext): void {
  for (const u of ctx.allUnits) {
    if (u.faction !== Faction.Player || !u.alive || u.isDowned) continue;
    u.attackBoostTimer = Math.max(u.attackBoostTimer, SKILL_SURGE_DURATION);
  }
  ctx.triggerPulse(SKILL_PULSE_COLOUR.engine!, 2.2);
}

/**
 * Bassline (Sustain — Alex)
 * Heal nearby allies. When Alex is within SKILL_BASSLINE_VENUE_RANGE tiles of a
 * claimed player venue, also injects Sales and Gear (Venue Bassline bonus).
 */
function bassline(caster: Unit, ctx: SkillContext): void {
  for (const u of ctx.allUnits) {
    if (u.faction !== Faction.Player || !u.alive || u.isDowned) continue;
    if (tileDist(caster, u) <= SKILL_HEAL_RADIUS) {
      u.hp = Math.min(u.maxHp, u.hp + Math.round(u.maxHp * SKILL_HEAL_RATIO));
    }
  }
  if (ctx.resources && ctx.playerVenuePositions) {
    const nearVenue = ctx.playerVenuePositions.some(v =>
      Math.hypot(caster.gridPos.col - v.col, caster.gridPos.row - v.row) <= SKILL_BASSLINE_VENUE_RANGE,
    );
    if (nearVenue) {
      ctx.resources.add('sales', SKILL_BASSLINE_VENUE_SALES);
      ctx.resources.add('gear',  SKILL_BASSLINE_VENUE_GEAR);
      ctx.showToast?.(`Alex: Venue Bassline! (+${SKILL_BASSLINE_VENUE_SALES} Sales +${SKILL_BASSLINE_VENUE_GEAR} Gear)`, 'success');
    }
  }
  ctx.triggerPulse(SKILL_PULSE_COLOUR.sustain!, 1.8);
}

/**
 * Prophecy (Mystic — Jim)
 * Reveal fog within SKILL_PROPHECY_RADIUS tiles. When Jim is ≥
 * SKILL_PROPHECY_FAR_THRESHOLD tiles from the Beacon, radius doubles
 * (Deep Prophecy) — strongly rewards pushing Jim into unexplored territory.
 */
function prophecy(caster: Unit, ctx: SkillContext): void {
  let radius = SKILL_PROPHECY_RADIUS;
  let deep   = false;
  if (ctx.beaconPos) {
    const dist = Math.hypot(
      caster.gridPos.col - ctx.beaconPos.col,
      caster.gridPos.row - ctx.beaconPos.row,
    );
    if (dist >= SKILL_PROPHECY_FAR_THRESHOLD) {
      radius = SKILL_PROPHECY_FAR_RADIUS;
      deep   = true;
    }
  }
  ctx.fogSystem.revealRadius(caster.gridPos.col, caster.gridPos.row, radius);
  if (deep) ctx.showToast?.(`Jim: Deep Prophecy! (${radius}-tile reveal)`, 'success');
  ctx.triggerPulse(SKILL_PULSE_COLOUR.mystic!, deep ? 3.5 : 2.5);
}

/**
 * Future Verse (Songwriter — Nigel)
 * Nigel broadcasts a fragment of a song from the future.
 * — Stuns all enemies within SKILL_VERSE_RADIUS for SKILL_VERSE_STUN_DURATION (attack)
 * — Heals all friendly units within SKILL_VERSE_RADIUS for SKILL_VERSE_HEAL_RATIO × maxHp (defend)
 */
function futureVerse(caster: Unit, ctx: SkillContext): void {
  for (const u of ctx.allUnits) {
    if (!u.alive) continue;
    const d = tileDist(caster, u);
    if (d > SKILL_VERSE_RADIUS) continue;

    if (u.faction === Faction.Player && !u.isDowned) {
      // Heal nearby allies
      u.hp = Math.min(u.maxHp, u.hp + Math.round(u.maxHp * SKILL_VERSE_HEAL_RATIO));
    } else if (u.faction !== Faction.Player) {
      // Stun nearby enemies
      u.stunTimer = Math.max(u.stunTimer, SKILL_VERSE_STUN_DURATION);
    }
  }
  ctx.triggerPulse(SKILL_PULSE_COLOUR.songwriter!, 2.0);
}

// ── Utility ───────────────────────────────────────────────────────────────────

function tileDist(a: Unit, b: Unit): number {
  return Math.hypot(
    a.gridPos.col - b.gridPos.col,
    a.gridPos.row - b.gridPos.row,
  );
}
